import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../button/button.component';
import { Observable, Subscription } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { MicrosoftDialPlanInventoryReportResource } from './dial-plan-inventory-report.resource';
import {
  DialPlanGroupEntryStatus,
  DialPlanGroupInventoryEntry,
  JobState,
  JobStatus,
  PstnConnectivityType,
} from '../../../shared/models/generated/ziroModels';
import { filter, take } from 'rxjs/operators';
import { MicrosoftDialPlanInventoriesPollingService } from './dial-plan-inventory-report-polling.service';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';

interface MicrosoftDialPlanInventoryRow extends DatatableRow {
  groupName: string;
  percentAvailable: number;
  available: string;
  pstnConnectivityType: string;
}

enum InventoryThresholds {
  high = 10,
  low = 0,
}

enum InventoryStatus {
  HIGH = 'tkey;reports.microsoft.dial_plan_inventory.inventory_status.high',
  LOW = 'tkey;reports.microsoft.dial_plan_inventory.inventory_status.low',
  EMPTY = 'tkey;reports.microsoft.dial_plan_inventory.inventory_status.empty',
}

@Component({
  selector: 'ziro-microsoft-dial-plan-inventory-report',
  templateUrl: './dial-plan-inventory-report.component.html',
  styleUrls: ['../../reporting.scss'],
  providers: [MicrosoftDialPlanInventoryReportResource, MicrosoftDialPlanInventoriesPollingService],
  standalone: false,
})
export class MicrosoftDialPlanInventoryReportComponent implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _toastService = inject(ToastService);
  private _microsoftDialPlanInventoryReportResource = inject(MicrosoftDialPlanInventoryReportResource);
  private _microsoftDialPlanInventoriesPollingService = inject(MicrosoftDialPlanInventoriesPollingService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);

  private static _refreshButtonId = 'refreshReport';
  private static _exportButtonId = 'exportReport';
  private isRefreshing = signal(true);

  PSTN_CONNECTIVITY_TKEYS: Map<PstnConnectivityType, string> = new Map([
    [PstnConnectivityType.DIRECT_ROUTING, 'tkey;pstn_connectivity_type.direct_routing.label'],
    [PstnConnectivityType.MICROSOFT_CALLING_PLANS, 'tkey;pstn_connectivity_type.ms_calling_plans.label'],
    [PstnConnectivityType.OPERATOR_CONNECT, 'tkey;pstn_connectivity_type.operator_connect.label'],
    [PstnConnectivityType.ZIRO_DRAAS, 'tkey;pstn_connectivity_type.ziro_draas.label'],
    [PstnConnectivityType.ZIRO_DRAAS_BYOC, 'tkey;pstn_connectivity_type.ziro_draas_byoc.label'],
  ]);

  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ziroIcons = ZiroIcons;
  isLoading = true;
  tableDataAutomation = 'dial-plan-inventory-datatable';
  datatableRows = [] as MicrosoftDialPlanInventoryRow[];
  currentTenantId: string;
  availabilityFilterOptionsTexts: string[] = [];
  pstnConnectivityTypeFilterOptions: string[] = Array.from(this.PSTN_CONNECTIVITY_TKEYS.values()).map((tkey) =>
    this._translateService.instant(tkey),
  );
  datatableCols: DatatableColumn<MicrosoftDialPlanInventoryRow>[] = [
    {
      name: 'groupName',
      label: 'tkey;reports.microsoft.dial_plan_inventory.group_name.label',
    },
    {
      name: 'pstnConnectivityType',
      label: 'tkey;admin.dn_report.pstn_connectivity_type.label',
      columnCharacterWidth: 22,
      disableTooltip: true,
    },
    {
      name: 'available',
      label: 'tkey;reports.microsoft.dial_plan_inventory.available_numbers.label',
      sortFn: (a: string, b: string) => {
        // a and b are html <span>'s so need to extract the numbers:
        const numberA = Number(a.replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '').split('%')[0]);
        const numberB = Number(b.replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '').split('%')[0]);
        if (numberA < numberB) {
          return -1;
        } else if (numberA > numberB) {
          return 1;
        }
        return 0;
      },
    },
  ];
  shouldDisplayError = false;

  private _subs = new Subscription();

  constructor() {
    effect(() => {
      const isReportRefreshing = this.isRefreshing();
      this._setPending(MicrosoftDialPlanInventoryReportComponent._refreshButtonId, isReportRefreshing);
      this._setDisabled(MicrosoftDialPlanInventoryReportComponent._exportButtonId, isReportRefreshing);
    });
  }

  ngOnInit(): void {
    const tenantsSub = this._microsoftTenantsStateContext.cachedTenantState$.pipe(take(1)).subscribe((cachedTenant) => {
      this.currentTenantId = cachedTenant.tenantId.toString();
      this._initFilterOptions();
      this._getDialPlanInventories();
    });
    this._subs.add(tenantsSub);

    this._initBreadcrumbs();
    this._initBottomNav();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
    this._microsoftDialPlanInventoriesPollingService.stopPolling();
  }

  onRefreshClick(): void {
    this.isLoading = true;
    this._refreshAndPollDialPlanInventories();
  }

  onExportClick(): void {
    this.isRefreshing.set(true);
    this._microsoftDialPlanInventoryReportResource.export(this.currentTenantId).subscribe(() => {
      this.isRefreshing.set(false);
      this._toastService.push(
        ToastTypes.SUCCESS,
        this.ziroIcons.DOWNLOAD,
        'tkey;reports.xlsx_export.downloaded_toast.title',
        'tkey;reports.xlsx_export.downloaded_toast.message',
      );
    });
  }

  isAvailableNumbersMatch = (filters: string[], row: MicrosoftDialPlanInventoryRow): boolean => {
    if (!filters.length) {
      return true;
    } else {
      if (row.percentAvailable <= InventoryThresholds.low) {
        return filters.some((val: string) =>
          this._translateService.instant(val).includes(this._translateService.instant(InventoryStatus.EMPTY)),
        );
      } else if (row.percentAvailable > InventoryThresholds.low && row.percentAvailable < InventoryThresholds.high) {
        return filters.some((val: string) =>
          this._translateService.instant(val).includes(this._translateService.instant(InventoryStatus.LOW)),
        );
      } else if (row.percentAvailable >= InventoryThresholds.high) {
        return filters.some((val: string) =>
          this._translateService.instant(val).includes(this._translateService.instant(InventoryStatus.HIGH)),
        );
      }
    }
  };

  isPstnConnectivityTypeMatch = (filter: string[], row: MicrosoftDialPlanInventoryRow): boolean => {
    return (
      !filter.length || filter.some((filter) => filter === this._translateService.instant(row.pstnConnectivityType))
    );
  };

  private _getDialPlanInventories(): void {
    this._microsoftDialPlanInventoryReportResource.status(this.currentTenantId).subscribe((jobStatus: JobStatus) => {
      if (jobStatus.jobState === JobState.IDLE) {
        this.isRefreshing.set(false);

        if (jobStatus.lastRunTime) {
          this._setBottomNavTimestamp(jobStatus.lastRunTime);
          if (jobStatus.error) {
            this.shouldDisplayError = true;
            this.isLoading = false;
          } else {
            this._microsoftDialPlanInventoryReportResource.get(this.currentTenantId).subscribe((data) => {
              this._initDatatable(data);
              this.isLoading = false;
            });
          }
        } else {
          this._refreshAndPollDialPlanInventories();
        }
      } else {
        this.isRefreshing.set(true);
        this._pollForIdleStatus().subscribe(() => {
          this._microsoftDialPlanInventoriesPollingService.stopPolling();
          this.isRefreshing.set(false);
          if (jobStatus.error) {
            this.shouldDisplayError = true;
            this.isLoading = false;
          } else {
            this._microsoftDialPlanInventoryReportResource.get(this.currentTenantId).subscribe((data) => {
              this._initDatatable(data);
              this.isLoading = false;
            });
          }
        });
      }
    });
  }

  private _refreshAndPollDialPlanInventories(): void {
    this.isLoading = true;
    this._microsoftDialPlanInventoryReportResource.refresh(this.currentTenantId).subscribe(() => {
      this.isRefreshing.set(true);
      this._pollForIdleStatus().subscribe((jobStatus) => {
        this.isRefreshing.set(false);
        this._microsoftDialPlanInventoriesPollingService.stopPolling();
        if (jobStatus.error) {
          this.shouldDisplayError = true;
          this.isLoading = false;
        } else {
          this._microsoftDialPlanInventoryReportResource.get(this.currentTenantId).subscribe((data) => {
            this._initDatatable(data);
            this._toastService.push(
              ToastTypes.SUCCESS,
              this.ziroIcons.REFRESH,
              'tkey;reports.microsoft.dial_plan_inventory.toast.title',
              'tkey;reports.microsoft.dial_plan_inventory.toast.message',
            );

            this.isLoading = false;
          });
        }
      });
    });
  }

  private _pollForIdleStatus(): Observable<JobStatus> {
    this._microsoftDialPlanInventoriesPollingService.startPolling();
    return this._microsoftDialPlanInventoriesPollingService.state$.pipe(
      filter((jobStatus: JobStatus) => {
        if (jobStatus.jobState === JobState.IDLE) {
          this._setBottomNavTimestamp(jobStatus.lastRunTime);
          return true;
        }
        return false;
      }),
    );
  }

  private _initDatatable(data: DialPlanGroupInventoryEntry[]): void {
    const reducedData: { [groupName: string]: DialPlanGroupInventoryEntry[] } = data.reduce(
      (acc, curr) => {
        if (acc[curr.dialPlanGroupName]) {
          acc[curr.dialPlanGroupName].push(curr);
        } else {
          acc[curr.dialPlanGroupName] = [curr];
        }
        return acc;
      },
      {} as { [groupName: string]: DialPlanGroupInventoryEntry[] },
    );
    this.datatableRows = Object.values(reducedData).map((value) => {
      return this._resolveDialPlanGroupInventory(value);
    });
  }

  private _resolveDialPlanGroupInventory(
    dialPlanGroupInventory: DialPlanGroupInventoryEntry[],
  ): MicrosoftDialPlanInventoryRow {
    const total = dialPlanGroupInventory.length;
    const availableCount = dialPlanGroupInventory.filter((entry) => this._isAvailable(entry)).length;
    const percentAvailable = total === 0 ? 0 : Math.round((availableCount / total) * 100);

    let rowCssClass = 'table-warning';
    if (percentAvailable <= InventoryThresholds.low) {
      rowCssClass = 'table-danger';
    } else if (percentAvailable >= InventoryThresholds.high) {
      rowCssClass = '';
    }

    return {
      groupName: dialPlanGroupInventory[0].dialPlanGroupName,
      pstnConnectivityType: this.PSTN_CONNECTIVITY_TKEYS.get(dialPlanGroupInventory[0].pstnConnectivityType) ?? '',
      available: this._translateService.instant('tkey;reports.microsoft.dial_plan_inventory.available.text', {
        available: availableCount,
        total: total,
        percent: percentAvailable,
      }),
      percentAvailable: percentAvailable,
      cssClass: rowCssClass,
    };
  }

  private _isAvailable(entry: DialPlanGroupInventoryEntry): boolean {
    return (
      entry.dialPlanGroupEntry.assignmentStatus === DialPlanGroupEntryStatus.UNASSIGNED ||
      entry.dialPlanGroupEntry.assignmentStatus === DialPlanGroupEntryStatus.INACTIVE ||
      entry.dialPlanGroupEntry.assignmentStatus === DialPlanGroupEntryStatus.RECENTLY_DELETED
    );
  }

  private _initFilterOptions(): void {
    const inventoryFilterOptions = {
      high: this._translateService.instant(InventoryStatus.HIGH),
      low: this._translateService.instant(InventoryStatus.LOW),
      empty: this._translateService.instant(InventoryStatus.EMPTY),
    };
    this.availabilityFilterOptionsTexts = Object.values(inventoryFilterOptions);
  }

  private _initBreadcrumbs(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.microsoft.dial_plan_inventory.title' }]);
  }

  private _initBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [
      {
        id: MicrosoftDialPlanInventoryReportComponent._refreshButtonId,
        dataAutomation: MicrosoftDialPlanInventoryReportComponent._refreshButtonId,
        label: 'tkey;reports.microsoft.dial_plan_inventory.refresh_dial_plan_data',
        icon: this.ziroIcons.REFRESH,
        buttonClass: this.buttonStyles.DEFAULT,
        cb: () => {
          this.onRefreshClick();
        },
      },
      {
        id: MicrosoftDialPlanInventoryReportComponent._exportButtonId,
        dataAutomation: MicrosoftDialPlanInventoryReportComponent._exportButtonId,
        label: 'tkey;reports.microsoft.dial_plan_inventory.export_to_excel',
        icon: this.ziroIcons.EXPORT,
        buttonClass: this.buttonStyles.PRIMARY,
        cb: () => {
          this.onExportClick();
        },
      },
    ];

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _setBottomNavTimestamp(timestamp: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${this.ziroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.dial_plan_inventory.last_updated',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _setPending(id: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: id,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _setDisabled(id: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: id,
        state: {
          pending: false,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }
}
